import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import { resendVerification, verifyEmail } from "@/lib/auth-api";

/**
 * apps/web :: components :: VerificationCodeStep
 *
 * ROADS-504 — In-app code-entry UI for the signup verification flow.
 *
 * WorkOS API-only stance (no AuthKit, no WorkOS-hosted UI): users get
 * a 6-digit OTP in the verification email and paste it here. We POST it
 * to `/auth/verify-email`; on success the backend mints a session and
 * the parent re-renders into the authenticated state.
 *
 * The dead-end "check your email" screen this replaces meant signup
 * tested green on dev (auto-verified test users) but broke for real
 * accounts because there was nowhere to enter the code.
 */
interface VerificationCodeStepProps {
  /** Email the verification code was sent to — surfaced verbatim in the UI. */
  email: string;
  /**
   * WorkOS user id returned by /auth/signup. Used by the resend button to
   * call /auth/resend-verification. When blank (older backend response
   * without user_id), the resend affordance is hidden — the user can
   * still try the code they have.
   */
  userId: string;
  /**
   * ROADS-527 — `pending_authentication_token` returned by /auth/signup.
   * WorkOS pairs this token + the 6-digit code server-side; without it
   * the verify call returns 400 invalid_input. When blank (older backend
   * deploys before the token plumbing landed) the verify submit will
   * surface that error — the user has to re-trigger signup.
   */
  pendingAuthenticationToken: string;
  /** Invoked after a successful verifyEmail call. Parent owns post-verify nav. */
  onVerified: () => void;
}

const RESEND_COOLDOWN_S: number = 60;
const CODE_LENGTH: number = 6;

export default function VerificationCodeStep({
  email,
  userId,
  pendingAuthenticationToken,
  onVerified,
}: VerificationCodeStepProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number>(RESEND_COOLDOWN_S);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length < CODE_LENGTH) {
      setError(t("landing.verify.errorCodeRequired"));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await verifyEmail(code, pendingAuthenticationToken);
      onVerified();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 400
          ? t("landing.verify.errorBadCode")
          : t("landing.verify.errorGeneric");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!userId || resendCooldown > 0) return;
    setResendSent(false);
    setError(null);
    try {
      await resendVerification(userId);
      setResendSent(true);
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch {
      setError(t("landing.verify.errorResendFailed"));
    }
  }

  return (
    <div data-testid="verification-code-step" className="mt-8">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10">
        <svg
          className="h-6 w-6 text-brand-primary"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="mt-4 font-display text-xl font-bold text-brand-primary">
        {t("landing.verificationSentTitle")}
      </h2>
      <p className="mt-2 text-sm text-neutral-600">
        {t("landing.verificationSentBody", { email })}
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-3" noValidate>
        <label className="block">
          <span className="text-sm font-medium text-neutral-700">
            {t("landing.verify.codeLabel")}
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
            }
            data-testid="verification-code-input"
            aria-invalid={error !== null}
            aria-describedby={error !== null ? "verification-code-error" : undefined}
            disabled={submitting}
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-center font-mono text-lg tracking-[0.4em] text-brand-primary shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
          />
        </label>

        {error !== null && (
          <p
            id="verification-code-error"
            role="alert"
            data-testid="verification-code-error"
            className="text-sm text-danger"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || code.length < CODE_LENGTH}
          data-testid="verification-code-submit"
          className="w-full rounded-md bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? t("landing.verify.submitting") : t("landing.verify.submit")}
        </button>
      </form>

      <div className="mt-4 text-center text-sm text-neutral-600">
        {resendSent && (
          <p
            role="status"
            data-testid="verification-resend-sent"
            className="mb-2 text-success"
          >
            {t("landing.verify.resentToast")}
          </p>
        )}
        {userId && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0}
            data-testid="verification-resend"
            className="text-brand-primary hover:underline disabled:cursor-not-allowed disabled:text-neutral-400 disabled:no-underline"
          >
            {resendCooldown > 0
              ? t("landing.verify.resendCooldown", { seconds: resendCooldown })
              : t("landing.verify.resendCta")}
          </button>
        )}
      </div>
    </div>
  );
}
